import { z } from "zod";
import type { RuntimeErrorEvent } from "./runtime-errors";
import { RuntimeErrorEventSchema } from "./runtime-errors";

export const DeploymentStepStatusSchema = z.enum([
  "pending",
  "processing",
  "completed",
  "cancelled",
  "error",
]);
export type DeploymentStepStatus = z.infer<typeof DeploymentStepStatusSchema>;

export const DeploymentStepSchema = z.object({
  id: z.string().nanoid(),
  name: z.string(),
  status: DeploymentStepStatusSchema,
  logs: z.string().optional(),
  startedAt: z.coerce
    .date()
    .optional()
    .describe("Time when the step went into the status processing"),
  finishedAt: z.coerce
    .date()
    .optional()
    .describe(
      "Time when the step went into the status completed, cancelled, or error",
    ),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DeploymentStep = z.infer<typeof DeploymentStepSchema>;

export const DeploymentStatusSchema = z.enum([
  "pending",
  "processing",
  "completed",
  "cancelled",
  "error",
]);
export type DeploymentStatus = z.infer<typeof DeploymentStatusSchema>;

export const DeploymentSchema = z.object({
  id: z.string().nanoid(),
  status: DeploymentStatusSchema,
  steps: DeploymentStepSchema.array(),
  versionId: z.string().nanoid(),
  specId: z.string().nanoid(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Deployment = z.infer<typeof DeploymentSchema>;

export const DeploymentSummarySchema = z.object({
  id: z.string().nanoid(),
  status: DeploymentStatusSchema,
  versionId: z.string().nanoid(),
  specId: z.string().nanoid(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DeploymentSummary = z.infer<typeof DeploymentSummarySchema>;

export const FpFileSchema = z.object({
  id: z.string().nanoid(),
  filename: z.string(),
  path: z.string().optional(), // For code files, represents their location in the filesystem
  content: z.string().optional(),
  status: z.enum(["processing", "completed"]),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type FpFile = z.infer<typeof FpFileSchema>;

export const FpVersionSchema = z.object({
  id: z.string().nanoid(),
  version: z.number().int().min(1),
  files: FpFileSchema.array(),
  specId: z.string().nanoid(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type FpVersion = z.infer<typeof FpVersionSchema>;

const FpTextMessagePartSchema = z.object({
  type: z.literal("text"),
  id: z.string(), // Unique ID for streaming updates
  content: z.string(),
  status: z.enum(["streaming", "complete", "cancelled"]).optional(),
});

const FpToolCallMessagePartSchema = z.object({
  type: z.literal("tool-call"),
  id: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.unknown()),
  status: z
    .enum(["pending", "executing", "complete", "error", "cancelled"])
    .optional(),
  result: z.unknown().optional(),
});
export type FpToolCallMessagePart = z.infer<typeof FpToolCallMessagePartSchema>;

export const FpMessagePartSchema = z.discriminatedUnion("type", [
  FpTextMessagePartSchema,
  FpToolCallMessagePartSchema,
]);
export type FpMessagePart = z.infer<typeof FpMessagePartSchema>;

export const FpMessageSchema = z.object({
  id: z.string().nanoid(),
  sender: z.enum(["user", "agent"]),
  parentMessageId: z.string().nanoid().optional(),
  content: z.string(),

  spec: FpFileSchema.optional(),
  version: FpVersionSchema.optional(),
  parts: FpMessagePartSchema.array().optional(), // New field for streaming parts
  usesParts: z.boolean().optional(), // Flag to indicate this message uses parts rendering

  status: z.enum(["pending", "processing", "completed", "cancelled"]),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FpMessage = z.infer<typeof FpMessageSchema>;

export const FpChatSchema = z
  .object({
    id: z.string().nanoid(),
    summary: z
      .string()
      .nullable()
      .optional()
      .describe("A short description based on the chat session"),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .describe("A single chat object");

export type FpChat = z.infer<typeof FpChatSchema>;

export const GetChatResponseSchema = z
  .object({
    chat: FpChatSchema,
  })
  .describe("Response payload associated with the chat get endpoint");

export type GetChatResponse = z.infer<typeof GetChatResponseSchema>;

export const ListMessagesResponseSchema = z
  .object({
    messages: FpMessageSchema.array(),
  })
  .describe("Response payload associated with the message list endpoint");

export type ListMessagesResponse = z.infer<typeof ListMessagesResponseSchema>;

export const GetMessageResponseSchema = z
  .object({
    message: FpMessageSchema,
  })
  .describe("Response payload associated with the message get endpoint");

export type GetMessageResponse = z.infer<typeof GetMessageResponseSchema>;

export const CreateChatRequestSchema = z
  .object({
    summary: z
      .string()
      .optional()
      .describe(
        "Initial summary set to the chat session, leave empty to auto generate a text",
      ),
  })
  .describe("Request payload associated with the chat create endpoint");
export type CreateChatRequest = z.infer<typeof CreateChatRequestSchema>;

export const CreateChatResponseSchema = z
  .object({
    chat: FpChatSchema,
  })
  .describe("Response payload associated with the chat create endpoint");
export type CreateChatResponse = z.infer<typeof CreateChatResponseSchema>;

export const ListChatResponseSchema = z
  .object({
    chats: FpChatSchema.array(),
  })
  .describe("Response payload associated with the chat create endpoint");
export type ListChatResponse = z.infer<typeof ListChatResponseSchema>;

// FpUser -> Codegen

export const FpUserMessageCreateSchema = z.object({
  type: z.literal("user.message.create"),

  messageId: z.string().nanoid(),
  content: z.string(),
});
export type FpUserMessageCreate = z.infer<typeof FpUserMessageCreateSchema>;

export const FpUserCancelSchema = z.object({
  type: z.literal("user.cancel"),

  messageId: z.string().nanoid(),
});
export type FpUserCancel = z.infer<typeof FpUserCancelSchema>;

export const AddMcpServerSchema = z.object({
  type: z.literal("mcp.server.add"),
  url: z.string().url(),
  name: z.string(),
});
export type AddMcpServer = z.infer<typeof AddMcpServerSchema>;

export const RemoveMcpServerSchema = z.object({
  type: z.literal("mcp.server.remove"),
  serverId: z.string(),
});
export type RemoveMcpServer = z.infer<typeof RemoveMcpServerSchema>;

// Codegen -> FpUser
export const FpAgentMessageCreatedSchema = z.object({
  type: z.literal("agent.message.created"),

  message: FpMessageSchema,
});
export type FpAgentMessageCreated = z.infer<typeof FpAgentMessageCreatedSchema>;

export const FpAgentMessageUpdatedSchema = z.object({
  type: z.literal("agent.message.updated"),

  message: FpMessageSchema,
});
export type FpAgentMessageUpdated = z.infer<typeof FpAgentMessageUpdatedSchema>;

export function createFpAgentMessageUpdate(
  message: FpMessage,
): FpAgentMessageUpdated {
  return {
    type: "agent.message.updated",
    message,
  };
}

/**
 * @deprecated - this is not needed anymore, we use parts instead
 * @todo - remove (and also remove corresponding logic on frontend)
 */
export const FpAgentContentAppendedSchema = z.object({
  type: z.literal("agent.message.content.appended"),

  messageId: z.string().nanoid(),
  content: z.string(),
});
/**
 * @deprecated - this is not needed anymore, we use parts instead
 * @todo - remove (and also remove corresponding logic on frontend)
 */
export type FpAgentContentAppended = z.infer<
  typeof FpAgentContentAppendedSchema
>;

export const FpFileCreatedSchema = z.object({
  type: z.literal("agent.file.created"),
  file: FpFileSchema,
  messageId: z.string().nanoid(),
});
export type FpFileCreated = z.infer<typeof FpFileCreatedSchema>;

export function createFpFileCreated(
  file: FpFile,
  messageId: string,
): FpFileCreated {
  return {
    type: "agent.file.created",
    file,
    messageId,
  };
}

export const FpFileUpdatedSchema = z.object({
  type: z.literal("agent.file.updated"),
  file: FpFileSchema,
  messageId: z.string().nanoid(),
});
export type FpFileUpdated = z.infer<typeof FpFileUpdatedSchema>;

export function createFpFileUpdated(
  file: FpFile,
  messageId: string,
): FpFileUpdated {
  return {
    type: "agent.file.updated",
    file,
    messageId,
  };
}

export const FpVersionCreatedSchema = z.object({
  type: z.literal("agent.version.created"),
  version: FpVersionSchema,
  messageId: z.string().nanoid(),
});
export type FpVersionCreated = z.infer<typeof FpVersionCreatedSchema>;

export function createFpVersionCreated(
  version: FpVersion,
  messageId: string,
): FpVersionCreated {
  return {
    type: "agent.version.created",
    version,
    messageId,
  };
}

export const FpAgentErrorSchema = z.object({
  type: z.literal("agent.error"),

  messageId: z.string().nanoid().optional(),
  level: z.enum(["error", "fatal"]),
  error: z.string(),
});
export type FpAgentError = z.infer<typeof FpAgentErrorSchema>;

export const SecretKeySchema = z.string().describe("Identifier for a secret");
export type SecretKey = z.infer<typeof SecretKeySchema>;

export const SecretValueSchema = z.string().describe("Value for a secret");
export type SecretValue = z.infer<typeof SecretValueSchema>;

export const EncryptedSecretValueSchema = z
  .string()
  .describe("Value for a encrypted secret");
export type EncryptedSecretValue = z.infer<typeof EncryptedSecretValueSchema>;

export const DecryptedSecretSchema = z
  .object({
    key: SecretKeySchema,
    decrypted_value: SecretValueSchema,
    isEmpty: z.boolean().describe("Indicates if the secret value is empty"),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .describe("Summary of a secret, does not include the actual secret value");
export type DecryptedSecret = z.infer<typeof DecryptedSecretSchema>;

export const SecretSummarySchema = z
  .object({
    key: SecretKeySchema,
    isEmpty: z.boolean().describe("Indicates if the secret value is empty"),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .describe("Summary of a secret, does not include the actual secret value");
export type SecretSummary = z.infer<typeof SecretSummarySchema>;

export const GetSecretsResponseSchema = z
  .object({
    secretKeys: SecretKeySchema.array().describe(
      "List of the secret keys defined",
    ),
    secrets: SecretSummarySchema.array(),
  })
  .describe(
    "Response payload associated with the secret get endpoint. Note that the actual value is never returned",
  );
export type GetSecretsResponse = z.infer<typeof GetSecretsResponseSchema>;

export const EndpointSchema = z
  .object({
    value: z.string().describe("Unique identifier for the endpoint"),
    method: z
      .enum(["GET", "POST", "PUT", "PATCH", "DELETE", "OPTIONS", "HEAD"])
      .describe("HTTP method"),
    path: z.string().describe("API endpoint path"),
    statusCode: z
      .number()
      .int()
      .describe("Default status code for the endpoint"),
    pathParams: z
      .array(z.string())
      .describe("Path parameters extracted from the endpoint path"),
    queryParams: z
      .array(z.string())
      .describe("Query parameters defined in the OpenAPI spec"),
    requiredHeaders: z
      .array(z.string())
      .describe("Required headers defined in the OpenAPI spec"),
    bodySchema: z
      .unknown()
      .describe("Request body schema from the OpenAPI spec"),
  })
  .describe("An API endpoint definition");
export type Endpoint = z.infer<typeof EndpointSchema>;

export const GetEndpointsResponseSchema = z
  .object({
    endpoints: EndpointSchema.array(),
    error: z
      .object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
      })
      .optional(),
  })
  .describe("Response payload associated with the endpoints get endpoint");
export type GetEndpointsResponse = z.infer<typeof GetEndpointsResponseSchema>;

export const FetchEndpointRequestDataSchema = z.object({
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE", "OPTIONS", "HEAD"]),
  path: z
    .string()
    .describe("API endpoint path (e.g., '/users' or '/users/123')"),
  params: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  headers: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
  body: z.string().optional(),
});
export type FetchEndpointRequestData = z.infer<
  typeof FetchEndpointRequestDataSchema
>;

export const FetchEndpointResponseSchema = z.object({
  status: z.number(),
  statusText: z.string(),
  headers: z.record(z.string()),
  body: z.string(),
  url: z.string(),
  method: z.string(),
  duration: z.number(),
});
export type FetchEndpointResponse = z.infer<typeof FetchEndpointResponseSchema>;

export const SerializableMcpConnectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  connectionState: z.enum([
    "connecting",
    "ready",
    "failed",
    "authenticating",
    "discovering",
  ]),
  url: z.string(),
  error: z.string().optional(),
  authUrl: z.string().optional(),
  capabilities: z
    .object({
      tools: z.array(z.string()).optional(),
      resources: z.array(z.string()).optional(),
    })
    .optional(),
  createdAt: z.coerce.date().optional(),
  lastConnected: z.coerce.date().optional(),
});
export type SerializableMcpConnection = z.infer<
  typeof SerializableMcpConnectionSchema
>;

export const GetMcpServersResponseSchema = z
  .object({
    connections: z.record(z.string(), SerializableMcpConnectionSchema),
  })
  .describe(
    "Response payload for the MCP servers endpoint containing active server connections",
  );
export type GetMcpServersResponse = z.infer<typeof GetMcpServersResponseSchema>;

// Task Management Schemas
import { TaskPrioritySchema, type TaskStatus, TaskStatusSchema } from "./tasks";

export const AgentTaskSchema = z.object({
  id: z.string().nanoid(),
  title: z.string(),
  description: z.string().optional(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  sortOrder: z.number().int(),
  metadata: z.record(z.unknown()).optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AgentTask = z.infer<typeof AgentTaskSchema>;

export const GetTasksResponseSchema = z
  .object({
    tasks: AgentTaskSchema.array(),
  })
  .describe(
    "Response payload for the tasks endpoint containing tasks for a message",
  );
export type GetTasksResponse = z.infer<typeof GetTasksResponseSchema>;

// Task Management Tool Result Schemas
export const CreateTasksResultSchema = z.object({
  tasksCreated: z.number(),
  tasks: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      description: z.string().optional(),
      priority: TaskPrioritySchema,
      sortOrder: z.number(),
    }),
  ),
});
export type CreateTasksResult = z.infer<typeof CreateTasksResultSchema>;

export const GetTasksToolResultSchema = z.object({
  tasks: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      description: z.string().optional(),
      status: TaskStatusSchema,
      priority: TaskPrioritySchema,
      sortOrder: z.number(),
      createdAt: z.string(),
      updatedAt: z.string(),
      startedAt: z.string().optional(),
      completedAt: z.string().optional(),
    }),
  ),
});
export type GetTasksToolResult = z.infer<typeof GetTasksToolResultSchema>;

export const UpdateTaskResultSchema = z.object({
  taskId: z.string(),
  updated: z.boolean(),
  taskTitle: z.string(),
  newStatus: TaskStatusSchema.optional(),
});
export type UpdateTaskResult = z.infer<typeof UpdateTaskResultSchema>;

export const GetNextTaskResultSchema = z.object({
  hasNextTask: z.boolean(),
  task: z
    .object({
      id: z.string(),
      title: z.string(),
      description: z.string().optional(),
      priority: TaskPrioritySchema,
      sortOrder: z.number(),
    })
    .optional(),
  message: z.string().optional(),
});
export type GetNextTaskResult = z.infer<typeof GetNextTaskResultSchema>;

export const CheckTasksCompletedResultSchema = z.object({
  allTasksCompleted: z.boolean(),
});
export type CheckTasksCompletedResult = z.infer<
  typeof CheckTasksCompletedResultSchema
>;

export const GetTaskStatsResultSchema = z.object({
  total: z.number(),
  pending: z.number(),
  inProgress: z.number(),
  completed: z.number(),
  cancelled: z.number(),
});
export type GetTaskStatsResult = z.infer<typeof GetTaskStatsResultSchema>;

export const SetSecretSchema = z
  .object({
    type: z.literal("secret.set"),
    secretKey: SecretKeySchema,
    secretValue: SecretValueSchema,
  })
  .describe(
    "Mutation to set a secret value, can either update an existing secret or create a new one",
  );
export type SetSecret = z.infer<typeof SetSecretSchema>;

export const DeleteSecretSchema = z
  .object({
    type: z.literal("secret.delete"),
    secretKey: SecretKeySchema,
  })
  .describe("Mutation to delete a secret by its key");
export type DeleteSecret = z.infer<typeof DeleteSecretSchema>;

export const SecretMutationSchema = z
  .discriminatedUnion("type", [SetSecretSchema, DeleteSecretSchema])
  .describe(
    "Mutations on a secret, currently supports setting or deleting a secret",
  );
export type SecretMutation = z.infer<typeof SecretMutationSchema>;

export const MutateSecretSchema = z
  .object({
    type: z.literal("secret.mutate"),
    mutations: SecretMutationSchema.array(),
  })
  .describe("Command to mutate a secret");
export type MutateSecret = z.infer<typeof MutateSecretSchema>;

export const SecretMutatedSchema = z
  .object({
    type: z.literal("secret.mutated"),
  })
  .describe("Event emitted when a secret is mutated");
export type SecretMutated = z.infer<typeof SecretMutatedSchema>;

export function createSecretMutated(): SecretMutated {
  return {
    type: "secret.mutated",
  };
}

export const McpServersUpdatedSchema = z
  .object({
    type: z.literal("mcp.servers.updated"),
    data: z.object({
      connections: z.record(z.unknown()),
    }),
  })
  .describe("Event emitted when MCP server connections are updated");
export type McpServersUpdated = z.infer<typeof McpServersUpdatedSchema>;

export function createMcpServersUpdated(
  connections: Record<string, unknown>,
): McpServersUpdated {
  return {
    type: "mcp.servers.updated",
    data: {
      connections,
    },
  };
}

export const McpServerAddFailedSchema = z
  .object({
    type: z.literal("mcp.server.add.failed"),
    url: z.string(),
    name: z.string(),
    error: z.string(),
  })
  .describe("Event emitted when adding an MCP server fails");
export type McpServerAddFailed = z.infer<typeof McpServerAddFailedSchema>;

export function createMcpServerAddFailed(
  url: string,
  name: string,
  error: string,
): McpServerAddFailed {
  return {
    type: "mcp.server.add.failed",
    url,
    name,
    error,
  };
}

export const FpAgentPartAddedSchema = z.object({
  type: z.literal("agent.message.part.added"),
  messageId: z.string().nanoid(),
  part: FpMessagePartSchema,
  index: z.number(),
});
export type FpAgentPartAdded = z.infer<typeof FpAgentPartAddedSchema>;

export function createFpAgentPartAdded(
  messageId: string,
  part: FpMessagePart,
  index: number,
): FpAgentPartAdded {
  return {
    type: "agent.message.part.added",
    messageId,
    part,
    index,
  };
}

export const FpAgentPartUpdatedSchema = z.object({
  type: z.literal("agent.message.part.updated"),
  messageId: z.string().nanoid(),
  partId: z.string(),
  updates: z.object({
    content: z.string().optional(), // Appended to existing content
    status: z.string().optional(),
    result: z.unknown().optional(),
  }),
});
export type FpAgentPartUpdated = z.infer<typeof FpAgentPartUpdatedSchema>;

export function createFpAgentPartUpdated(
  messageId: string,
  partId: string,
  updates: {
    content?: string;
    status?: string;
    result?: unknown;
  },
): FpAgentPartUpdated {
  return {
    type: "agent.message.part.updated",
    messageId,
    partId,
    updates,
  };
}

export const FpRuntimeErrorSchema = z.object({
  type: z.literal("runtime.error"),
  data: RuntimeErrorEventSchema,
});
export type FpRuntimeError = z.infer<typeof FpRuntimeErrorSchema>;

export function createFpRuntimeError(data: RuntimeErrorEvent): FpRuntimeError {
  return {
    type: "runtime.error",
    data,
  };
}

export const DeploymentCreatedSchema = z.object({
  type: z.literal("deployment.created"),
  deployment: DeploymentSchema,
});

export const DeploymentUpdatedSchema = z.object({
  type: z.literal("deployment.updated"),
  deployment: DeploymentSchema,
});

export const DeploymentLogAppendSchema = z.object({
  type: z.literal("deployment.log.append"),
  deploymentId: z.string().nanoid(),
  content: z.string(),
});

export const TasksCreatedSchema = z.object({
  type: z.literal("tasks.created"),
  tasks: AgentTaskSchema.array(),
});
export type TasksCreated = z.infer<typeof TasksCreatedSchema>;

export function createTasksCreated(tasks: AgentTask[]): TasksCreated {
  return {
    type: "tasks.created",
    tasks,
  };
}

export const TaskUpdatedSchema = z.object({
  type: z.literal("task.updated"),
  taskId: z.string().nanoid(),
  task: AgentTaskSchema,
  updates: z.object({
    status: TaskStatusSchema.optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
  }),
});
export type TaskUpdated = z.infer<typeof TaskUpdatedSchema>;

export function createTaskUpdated(
  taskId: string,
  task: AgentTask,
  updates: {
    status?: TaskStatus;
    title?: string;
    description?: string;
    metadata?: Record<string, unknown>;
  },
): TaskUpdated {
  return {
    type: "task.updated",
    taskId,
    task,
    updates,
  };
}

export const FpCodegenCommandsSchema = z.discriminatedUnion("type", [
  FpUserMessageCreateSchema,
  FpUserCancelSchema,
  MutateSecretSchema,
  AddMcpServerSchema,
  RemoveMcpServerSchema,
]);
export type FpCodegenCommands = z.infer<typeof FpCodegenCommandsSchema>;

export const FpCodegenEventsSchema = z.discriminatedUnion("type", [
  FpAgentMessageCreatedSchema,
  FpAgentMessageUpdatedSchema,
  FpAgentContentAppendedSchema,
  FpFileCreatedSchema,
  FpFileUpdatedSchema,
  FpVersionCreatedSchema,
  FpAgentErrorSchema,
  SecretMutatedSchema,
  McpServersUpdatedSchema,
  McpServerAddFailedSchema,
  FpAgentPartAddedSchema,
  FpAgentPartUpdatedSchema,
  FpRuntimeErrorSchema,
  DeploymentCreatedSchema,
  DeploymentUpdatedSchema,
  DeploymentLogAppendSchema,
  TasksCreatedSchema,
  TaskUpdatedSchema,
]);
export type FpCodegenEvents = z.infer<typeof FpCodegenEventsSchema>;

export const ListDeploymentsResponseSchema = z.object({
  deployments: DeploymentSummarySchema.array(),
});
export type ListDeploymentsResponse = z.infer<
  typeof ListDeploymentsResponseSchema
>;

export const GetDeploymentResponseSchema = z.object({
  deployment: DeploymentSchema,
});
export type GetDeploymentResponse = z.infer<typeof GetDeploymentResponseSchema>;
